import { useEffect, useMemo, useRef, useState } from 'react';

import { hasArrayChanged, hasLikeStatusData } from './helpers';
import { cleanupLikeMetaStore, initLikeIds, initLikeIdsWithSavedProduct } from './likeMetaManager';
import { orderStore } from './orderStore';
import { Config } from './types';

export { initLikeIds } from './likeMetaManager';

/**
 * 상품목록에서 기존에 유저가 찜하기 했던 상품목록 id을 가져와 store에 저장하는 hook
 *
 * @param config.data - 찜하기 여부를 확인해야하는 상품목록 데이터
 * @param config.convert - data에서 상품 id만 추출하는 함수
 * @param config.groupKeys - 그룹핑을 위한 키 배열
 * @param config.shouldSkipApiCall - API 호출 건너뛰기 플래그
 *
 * @docs https://www.notion.so/croquis/widget-like-product-bb6ac56bf316499ea5f85e30faadad03#8f9fcc93dc494be7893d31aea5ed5e7c
 */
export const useInitLikedProducts = <T>(config: Config<T>): void => {
  // 그룹 키 변경 감지
  const currentGroupKeys = config.groupKeys?.join() ?? '';
  const prevGroupKeys = useRef(currentGroupKeys);

  // 그룹 순서 ID 관리
  const [groupOrderId, setGroupOrderId] = useState(() => {
    const result = orderStore.getGroupOrder();
    orderStore.incGroupOrder();
    return result;
  });
  const prevGroupOrderId = useRef(groupOrderId);

  // 데이터 관리
  const data = useMemo(() => config.data, [config.data]);
  const latestData = useRef(data);
  latestData.current = data;

  // 변환 함수 관리
  const convert = useRef(config.convert);
  convert.current = config.convert;

  // 이전 목록 추적
  const prevList = useRef<string[]>([]);

  // 데이터 변경 처리
  useEffect(() => {
    if (!convert.current || !data) {
      return;
    }

    const currentList = convert.current(data);

    if (!hasArrayChanged(currentList, prevList.current)) {
      return;
    }

    handleDataChange(currentList, data, groupOrderId, config.shouldSkipApiCall);

    return () => {
      if (groupOrderId !== prevGroupOrderId.current) {
        prevList.current = currentList;
      }
    };
  }, [data, groupOrderId, config.shouldSkipApiCall]);

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => cleanupLikeMetaStore(groupOrderId);
  }, [groupOrderId]);

  // 그룹 키 변경 처리
  if (prevGroupKeys.current !== currentGroupKeys) {
    handleGroupKeyChange();
  }

  /**
   * 데이터 변경을 처리합니다.
   */
  function handleDataChange<TData>(
    ids: string[],
    dataSource: TData,
    groupId: number,
    shouldSkipApiCall?: boolean,
  ): void {
    const hasLikeData = hasLikeStatusData(dataSource);
    const shouldUseNewMethod = hasLikeData && !shouldSkipApiCall;

    if (shouldUseNewMethod) {
      initLikeIdsWithSavedProduct(ids, dataSource, groupId);
    } else {
      initLikeIds(ids, groupId, shouldSkipApiCall);
    }
  }

  /**
   * 그룹 키 변경을 처리합니다.
   */
  function handleGroupKeyChange(): void {
    prevGroupOrderId.current = orderStore.getGroupOrder();
    setGroupOrderId(() => {
      const result = orderStore.getGroupOrder();
      orderStore.incGroupOrder();
      return result;
    });
    prevGroupKeys.current = currentGroupKeys;
  }
};
